/**
 * Component: SignInTokenPage
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, AUTH-V1-R02
 *
 * Handles the SAML SSO token handoff from backend redirect.
 * Exchanges the short-lived JWT for a long-lived session token
 * via the /auth/saml/exchange endpoint, matching the original
 * archive RefreshAccessToken pattern.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/stores/auth-store';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import api from '@/lib/api';

interface ExchangeResponse {
  token: string;
  user: {
    userId: string;
    email: string;
    roles: string[];
    expiresAt: string;
  };
}

export function SignInTokenPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const processed = useRef(false);
  const { refetch: refetchFlags } = useFeatureFlags();

  useEffect(() => {
    // Prevent double-processing in React StrictMode
    if (processed.current) return;
    processed.current = true;

    if (!token) {
      setError('No authentication token provided');
      return;
    }

    // Exchange the short-lived SAML token for a long-lived session token
    // This matches the archive flow: short token (30s) -> POST to backend -> 7-day token
    const exchangeToken = async (): Promise<void> => {
      try {
        const response = await api.post<ExchangeResponse>(
          '/auth/saml/exchange',
          { token }
        );

        const { token: sessionToken, user } = response.data;

        // Store the long-lived session token
        localStorage.setItem('auth-token', sessionToken);

        // Update Zustand auth store
        useAuthStore.setState({
          user: {
            userId: user.userId,
            email: user.email,
            roles: user.roles,
            expiresAt: user.expiresAt,
          },
          isAuthenticated: true,
          error: null,
        });

        // Refetch feature flags now that we're authenticated
        void refetchFlags();
        // Navigate to app
        void navigate('/', { replace: true });
      } catch {
        setError(
          'Failed to complete sign-in. The token may have expired. Please try again.'
        );
      }
    };

    void exchangeToken();
  }, [token, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Sign-in Failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4 text-center">
            <a
              href="/login"
              className="text-sm text-primary underline hover:text-primary/80"
            >
              Return to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-sm text-muted-foreground">
          Completing sign-in...
        </p>
      </div>
    </div>
  );
}
